import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha, Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import {
  TravelExplore as ReconIcon,
  Psychology as AiIcon,
  Security as ScannerIcon,
  CloudQueue as CloudIcon,
  BugReport as IntelIcon,
  PhoneAndroid as MobileIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import { createApiKey, deleteApiKey, listApiKeys, testApiKeyConnection, updateApiKey } from 'api/apiKeys';
import { GREEN, MONO } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };

// ─── Provider catalog (branded color + icon + metadata) ───────────────────────
const PROVIDERS = [
  { provider: 'shodan',            name: 'Shodan',          color: '#e63946', icon: ReconIcon,   type: 'intel',   section: 'Recon / Enum',    usage: 'Passive intel — exposed ports, banners, known CVEs', docs: 'account.shodan.io' },
  { provider: 'virustotal',        name: 'VirusTotal',      color: '#394eff', icon: IntelIcon,   type: 'intel',   section: 'Threat Intel',    usage: 'Domain, URL, hash & malware reputation enrichment',  docs: 'virustotal.com/gui/my-apikey' },
  { provider: 'censys',            name: 'Censys',          color: '#00b3a4', icon: ReconIcon,   type: 'intel',   section: 'Attack Surface',  usage: 'Internet exposure, certificates, services, ASM',     docs: 'search.censys.io/account/api' },
  { provider: 'censys_organization_id', name: 'Censys Org ID', color: '#00b3a4', icon: ReconIcon, type: 'intel', section: 'Exposure Discovery', usage: 'Organization identifier required by newer Censys API flows', docs: 'search.censys.io/account/api' },
  { provider: 'fofa_email',        name: 'FOFA Email',      color: '#22c55e', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'FOFA account email for uncover passive exposure discovery', docs: 'fofa.info' },
  { provider: 'fofa',              name: 'FOFA API Key',    color: '#22c55e', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'FOFA API key for exposed host discovery', docs: 'fofa.info' },
  { provider: 'quake',             name: 'Quake',           color: '#0ea5e9', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: '360 Quake token for exposed asset discovery', docs: 'quake.360.net' },
  { provider: 'hunter',            name: 'Hunter',          color: '#14b8a6', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'Hunter API key for external exposure search', docs: 'hunter.qianxin.com' },
  { provider: 'zoomeye',           name: 'ZoomEye',         color: '#2563eb', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'ZoomEye API key for exposed services and hosts', docs: 'zoomeye.ai' },
  { provider: 'netlas',            name: 'Netlas',          color: '#16a34a', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'Netlas API key for internet asset intelligence', docs: 'netlas.io' },
  { provider: 'criminalip',        name: 'Criminal IP',     color: '#ef4444', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'Criminal IP API key for exposure and reputation enrichment', docs: 'criminalip.io' },
  { provider: 'publicwww',         name: 'PublicWWW',       color: '#f59e0b', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'PublicWWW API key for source and technology exposure queries', docs: 'publicwww.com' },
  { provider: 'hunterhow',         name: 'HunterHow',       color: '#8b5cf6', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'HunterHow API key for passive exposed asset discovery', docs: 'hunter.how' },
  { provider: 'google',            name: 'Google API Key',  color: '#4285f4', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'Google Custom Search API key for uncover queries', docs: 'developers.google.com/custom-search' },
  { provider: 'google_cx',         name: 'Google CX',       color: '#4285f4', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'Google Custom Search engine identifier', docs: 'programmablesearchengine.google.com' },
  { provider: 'onyphe',            name: 'Onyphe',          color: '#06b6d4', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'Onyphe API key for cyber defense search intelligence', docs: 'onyphe.io' },
  { provider: 'driftnet',          name: 'Driftnet',        color: '#64748b', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'Driftnet API key for external exposure discovery', docs: 'driftnet.io' },
  { provider: 'daydaymap',         name: 'DayDayMap',       color: '#f97316', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'DayDayMap API key for passive host discovery', docs: 'daydaymap.com' },
  { provider: 'nerdydata',         name: 'NerdyData',       color: '#a855f7', icon: ReconIcon,   type: 'intel',   section: 'Exposure Discovery', usage: 'NerdyData API key for source-code intelligence queries', docs: 'nerdydata.com' },
  { provider: 'nvd',               name: 'NVD',             color: '#f59e0b', icon: IntelIcon,   type: 'intel',   section: 'Vulnerabilities', usage: 'CVE enrichment & dependency vulnerability matching', docs: 'nvd.nist.gov/developers' },
  { provider: 'spyse',             name: 'Spyse',           color: '#8b5cf6', icon: ReconIcon,   type: 'intel',   section: 'Assetfinder',     usage: 'Optional assetfinder source (SPYSE_API_TOKEN)',      docs: 'spyse.com' },
  { provider: 'facebook_app_id',   name: 'Facebook App ID', color: '#1877f2', icon: ReconIcon,   type: 'intel',   section: 'Assetfinder',     usage: 'Optional assetfinder Facebook source (FB_APP_ID)',   docs: 'developers.facebook.com' },
  { provider: 'facebook_app_secret', name: 'Facebook Secret', color: '#1877f2', icon: ReconIcon, type: 'intel',  section: 'Assetfinder',     usage: 'Optional assetfinder Facebook source (FB_APP_SECRET)', docs: 'developers.facebook.com' },
  { provider: 'mobsf',             name: 'MobSF',           color: '#6366f1', icon: MobileIcon,  type: 'scanner', section: 'Mobile',          usage: 'Android / iOS static & dynamic mobile analysis',     docs: 'mobsf.github.io' },
  { provider: 'openai',            name: 'OpenAI',          color: '#10a37f', icon: AiIcon,      type: 'ai',      section: 'AI',              usage: 'Template generation, summaries, remediation drafts', docs: 'platform.openai.com/api-keys' },
  { provider: 'anthropic',         name: 'Anthropic',       color: '#d4915d', icon: AiIcon,      type: 'ai',      section: 'AI',              usage: 'AI-assisted analysis & security report drafting',    docs: 'console.anthropic.com' },
  { provider: 'cloud-audit',       name: 'Cloud Audit',     color: '#ff9900', icon: CloudIcon,   type: 'scanner', section: 'Cloud',           usage: 'Cloud posture & misconfiguration assessment',        docs: '' },
  { provider: 'custom-scanner',    name: 'Custom Scanner',  color: GREEN,     icon: ScannerIcon, type: 'scanner', section: 'Scanning',        usage: 'Any external scanner reachable through an API',       docs: '' }
];

const PROVIDER_BY_SLUG = Object.fromEntries(PROVIDERS.map((p) => [p.provider, p]));

const GROUPS = [
  { type: 'intel', title: 'Recon and intelligence', note: 'Find exposed hosts, certificates and known vulnerabilities' },
  { type: 'ai', title: 'AI', note: 'Drafting summaries and remediation text' },
  { type: 'scanner', title: 'Scanners', note: 'Mobile, cloud and your own scanners' },
  { type: 'other', title: 'Other', note: 'Credentials you added yourself' }
];

const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

const statusOf = (cred, test) => {
  if (!cred) return { label: 'NOT CONNECTED', color: '#64748b' };
  if (test?.ok === null) return { label: 'TESTING', color: '#f59e0b' };
  if (test?.ok === false) return { label: 'TEST FAILED', color: '#ef4444' };
  if (!cred.is_active) return { label: 'DISABLED', color: '#f59e0b' };
  if (test?.ok === true) return { label: 'WORKING', color: GREEN };
  return { label: 'CONNECTED', color: GREEN };
};

function Row({ preset, cred, test, busy, onAdd, onTest, onToggle, onDelete }) {
  const Icon = preset.icon;
  const st = statusOf(cred, test);
  const working = Boolean(busy);
  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ px: 2, py: 1.4, borderTop: 1, borderColor: 'divider' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'grid', placeItems: 'center', color: preset.color, bgcolor: alpha(preset.color, 0.12), flexShrink: 0 }}><Icon fontSize="small" /></Box>
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography fontWeight={600}>{preset.name}</Typography>
        <Typography variant="body2" color="text.secondary">{preset.usage}</Typography>
        {cred && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>{cred.masked_key || '••••••••'}{test?.message ? ` · ${test.message}` : ''}</Typography>}
        {!cred && preset.docs && <Typography variant="caption" color="text.secondary" display="block">Get a key at {preset.docs}</Typography>}
      </Box>
      <Pill color={st.color}>{st.label}</Pill>
      <Stack direction="row" gap={0.8}>
        {!cred ? (
          <Button size="small" variant="outlined" color="inherit" onClick={() => onAdd(preset)} disabled={working}>Add key</Button>
        ) : (
          <>
            <Button size="small" variant="outlined" color="inherit" onClick={() => onTest(cred)} disabled={working}>{busy === `test-${cred.id}` ? 'Testing…' : 'Test'}</Button>
            <Button size="small" color="inherit" onClick={() => onToggle(cred)} disabled={working}>{cred.is_active ? 'Disable' : 'Enable'}</Button>
            <Button size="small" color="error" onClick={() => onDelete(cred)} disabled={working}>Delete</Button>
          </>
        )}
      </Stack>
    </Stack>
  );
}

// ─── Add-Key Dialog ───────────────────────────────────────────────────────────
function AddKeyDialog({ open, preset, onClose, onSave, saving }) {
  const [form, setForm] = useState({ name: '', type: 'intel', provider: '', base_url: '', key_value: '', notes: '' });
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    if (open && preset) {
      setForm({ name: preset.name, type: preset.type, provider: preset.provider, base_url: '', key_value: '', notes: preset.usage });
      setShowKey(false);
    }
  }, [open, preset]);

  const valid = form.name.trim() && form.key_value.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Connect {preset?.name || 'a provider'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField size="small" label="Display name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField select size="small" label="Type" value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} sx={{ flex: 1 }}>
              <MenuItem value="ai">AI Provider</MenuItem>
              <MenuItem value="scanner">External Scanner</MenuItem>
              <MenuItem value="intel">Threat Intel</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField size="small" label="Provider slug" value={form.provider} onChange={(e) => setForm((p) => ({ ...p, provider: e.target.value }))} sx={{ flex: 1 }} />
          </Stack>
          <TextField size="small" label="Base URL (optional)" value={form.base_url} onChange={(e) => setForm((p) => ({ ...p, base_url: e.target.value }))} fullWidth />
          <TextField
            size="small" label="API key or token" type={showKey ? 'text' : 'password'} fullWidth
            value={form.key_value} onChange={(e) => setForm((p) => ({ ...p, key_value: e.target.value }))}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label={showKey ? 'Hide key' : 'Show key'} onClick={() => setShowKey((v) => !v)}>
                    {showKey ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField size="small" label="Notes" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={() => onSave(form)} variant="contained" disabled={!valid || saving} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>
          {saving ? 'Saving…' : 'Save and connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ApiKeysPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testStatus, setTestStatus] = useState({});
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState({ open: false, preset: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const data = await listApiKeys();
      setRows(data.results || data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load credentials.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const credByProvider = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const slug = (r.provider || '').toLowerCase();
      if (slug && !m[slug]) m[slug] = r;
    });
    return m;
  }, [rows]);

  // One list of entries: every catalog provider, then the credentials that are not in the catalog.
  const entries = useMemo(() => {
    const custom = rows
      .filter((r) => !PROVIDER_BY_SLUG[(r.provider || '').toLowerCase()])
      .map((cred) => ({
        preset: { ...PROVIDER_BY_SLUG['custom-scanner'], name: cred.name, provider: cred.provider, color: '#a855f7', type: ['ai', 'scanner', 'intel'].includes(cred.type) ? cred.type : 'other', usage: cred.notes || 'Custom integration', docs: '' },
        cred
      }));
    return [...PROVIDERS.map((preset) => ({ preset, cred: credByProvider[preset.provider] })), ...custom];
  }, [rows, credByProvider]);

  const connected = entries.filter((e) => e.cred).length;
  const active = rows.filter((r) => r.is_active).length;
  const working = Object.values(testStatus).filter((t) => t.ok === true).length;

  const visible = entries.filter(({ preset, cred }) => (
    (show === 'all' || (show === 'connected') === Boolean(cred))
    && (!q.trim() || `${preset.name} ${preset.usage} ${preset.section}`.toLowerCase().includes(q.trim().toLowerCase()))
  ));

  const handleSave = async (form) => {
    if (!form.name.trim() || !form.key_value.trim()) return;
    setBusy('save');
    try {
      await createApiKey({ ...form, is_active: true });
      setDialog({ open: false, preset: null });
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to save credential.');
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async (cred) => {
    setBusy(`test-${cred.id}`);
    setTestStatus((p) => ({ ...p, [cred.id]: { ok: null, message: 'Testing connection…' } }));
    try {
      const res = await testApiKeyConnection(cred.id);
      setTestStatus((p) => ({ ...p, [cred.id]: { ok: Boolean(res?.ok), message: res?.message || 'Connection test completed.' } }));
    } catch (e) {
      setTestStatus((p) => ({ ...p, [cred.id]: { ok: false, message: e?.message || 'Connection test failed.' } }));
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (cred) => {
    setBusy(`toggle-${cred.id}`);
    try {
      await updateApiKey(cred.id, { is_active: !cred.is_active });
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to update credential.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const cred = confirmDelete;
    setConfirmDelete(null);
    setBusy(`del-${cred.id}`);
    try {
      await deleteApiKey(cred.id);
      setTestStatus((p) => { const n = { ...p }; delete n[cred.id]; return n; });
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to delete credential.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>API keys</Typography>
          <Typography color="text.secondary">
            {connected === 0 ? 'No provider connected yet' : `${connected} connected, ${active} active${working ? `, ${working} tested working` : ''}`}. Keys are stored masked and belong to your account only.
          </Typography>
        </Box>
        <Button variant="outlined" color="inherit" onClick={() => setDialog({ open: true, preset: { ...PROVIDER_BY_SLUG['custom-scanner'], name: '', provider: '' } })}>Add a custom key</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center" sx={{ mb: 2.5 }}>
        <TextField size="small" placeholder="Search providers" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }} />
        <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v) => v && setShow(v)}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="connected">Connected</ToggleButton>
          <ToggleButton value="not">Not connected</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {loading && rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: GREEN }} /></Box>
      ) : (
        <>
          {GROUPS.map((g) => {
            const list = visible.filter((e) => e.preset.type === g.type);
            if (!list.length) return null;
            return (
              <Box key={g.type} sx={{ ...panel, mb: 2.5, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography fontWeight={700}>{g.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{g.note}</Typography>
                </Box>
                {list.map(({ preset, cred }) => (
                  <Row
                    key={cred ? `c-${cred.id}` : preset.provider}
                    preset={preset} cred={cred} test={cred ? testStatus[cred.id] : null} busy={busy}
                    onAdd={(p) => setDialog({ open: true, preset: p })}
                    onTest={handleTest} onToggle={handleToggle} onDelete={setConfirmDelete}
                  />
                ))}
              </Box>
            );
          })}
          {visible.length === 0 && <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No provider matches.</Typography>}
        </>
      )}

      <AddKeyDialog
        open={dialog.open}
        preset={dialog.preset}
        saving={busy === 'save'}
        onClose={() => setDialog({ open: false, preset: null })}
        onSave={handleSave}
      />

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete {confirmDelete?.name}?</DialogTitle>
        <DialogContent><Typography color="text.secondary">The key is removed from FuzzScan. Anything that used it stops working until you add a key again.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmDelete(null)}>Keep it</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
